/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import classnames from 'classnames';
import {cloneDeep, isUndefined} from 'lodash';
import {translate} from '@ion/core/src/locale/locale';
import {
  CreateJob,
  DeviceFile,
  DownloadJobTasksElem,
  PlatformDeviceTypes
} from '@ion/core/src/types/entities';
import {removeItemFromArray} from '@ion/core/src/utils/ArrayUtil';
import Render from '@ion/dls/src/render/Render';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {isRequiredField} from '@ion/query/src/hooks/useDeviceTypes';
import {errorOptions} from '../../prompts/constants';
import {toggleUserPrompt} from '../../store/actions/user-prompt';
import TasksDraggableList from './dependentTaskDnd/TasksDraggableList';
import DependentTaskChooser from './DependentTaskChooser';
import {
  fileTypeUsesAppIds,
  getAncestorIds,
  getDependentTaskOptions,
  getFileAppIds,
  getFileNameDisplay,
  reorderTaskDependencies
} from './helpers';
import JobTaskAppIdChooser from './JobTaskAppIdChooser';
import JobTaskDeviceFileTypeChooser from './JobTaskDeviceFileTypeChooser';
import JobTaskFileChooser from './JobTaskFileChooser';
import JobTaskOrder from './JobTaskOrder';
import JobTaskRowEditor from './JobTaskRowEditor';

export interface JobTasksGridProps {
  tasks?: CreateJob;
  setTasks?: (tasks: CreateJob) => void;
  onUpdateTasks?: (tasks: CreateJob) => void;
  deviceTypes?: PlatformDeviceTypes;
  deviceAppIds?: string[];
  accountId?: number;
  includeSubAccounts?: boolean;
  setJobNameUpdateEnabled?: (jobNameUpdateEnabled: boolean) => void;
  devicePlatformIds?: Array<Number>;
  isDevicesSelected?: boolean;
  isFetching?: boolean;
};

enum TaskField {
  DEVICE_TYPE= 'deviceType',
  FILE_TYPE = 'fileType',
  PLATFORM_ID = 'platformId',
  USES_APP_IDS = 'usesAppIds',
  APP_ID = 'appId',
  FILE_APP_IDS = 'fileAppIds',
  FILE_NAME = 'fileName',
  META_DATA = 'metaData',
  CHILDREN = 'children',
  VERSION = 'version'
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  actionsTile : {
    display         : 'flex',
    justifyContent  : 'space-between',
    backgroundColor : 'white',
    alignItems      : 'center',
    boxShadow       : `0px 4px 4px ${dlsColor('grey5')}`
  },
  tableContainer : {
    width    : '100%',
    overflow : 'unset'
  },
  tableHeader : {
    border     : `2px solid ${dlsColor('grey6')}`,
    background : dlsColor('grey8')
  },
  tableBody : {
    background : dlsColor('white')
  },
  tableRow : {
    position     : 'relative',
    borderBottom : `2px solid ${dlsColor('grey8')}`
  },
  tableCell : {
    ...dlsTypography('h4'),
    fontSize : '13px !important',
    color    : dlsColor('grey1'),
    padding  : '10px !important'
  },
  deviceTypeHeaderCell : {
    width : '24%'
  },
  taskOrder : {
    width        : '50px',
    paddingLeft  : '8px !important',
    paddingRight : '8px !important'
  },
  appIdHeaderCell : {
    width : '17%'
  },
  dependentTaskHeaderCell : {
    width                          : '15%',
    [theme.breakpoints.down('md')] : {
      width : '12%'
    }
  },
  dragIconCell : {
    width   : '0px !important',
    padding : '0px 8px !important'
  },
  actionHeaderCell : {
    width   : '0px !important',
    padding : '0px !important'
  },
  actionRowCell : {
    paddingLeft  : '0px !important',
    paddingRight : '0px !important'
  },
  loader : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'flex-start',
    position       : 'relative',
    left           : '256px',
    height         : '100px',
    paddingTop     : '15px'
  }
}));

/**
 * The JobTasksGrid provides the UI which allows a user to add/update tasks...
 *
 *    import {JobTasksGrid} from '@ion/applications/src/components/jobs/JobTasksGrid';
 *
 *    <JobTasksGrid />
 *
 */
export const JobTasksGrid = ({
  tasks,
  setTasks,
  onUpdateTasks,
  deviceTypes,
  deviceAppIds,
  accountId,
  includeSubAccounts,
  setJobNameUpdateEnabled,
  devicePlatformIds,
  isFetching = false,
  isDevicesSelected = true
}: JobTasksGridProps) => {
  const dispatch                                        = useDispatch();
  const classes                                         = useStyles();
  const [taskValue, updateTaskValue]                    = useState({});
  const [suppressNullVersions, setSuppressNullVersions] = useState(false);
  const allParentTasksRef                               = useRef([]);

  const fieldTranslations = translate('jobcreate:fields', {returnObjects: true});

  useEffect(() => {
    const items = tasks?.tasks;
    if (items?.length) {
      const values = {};
      items.forEach(({task: {deviceType, fileType}}, i) => {
        values[i] = (deviceType && fileType) ? `${deviceType} ${fileType}` : '';
      });
      updateTaskValue(values);
    }
  }, [tasks]);

  useEffect(() => {
    const jobTasks = tasks?.tasks;
    if (jobTasks?.length) {
      let tasksUpdated: boolean = false;
      const incompatibleFiles: string[] = [];
      jobTasks.forEach(({task}) => {
        const dependencyIndex = task.metaData?.dependency?.index;
        const parentTaskId    = jobTasks[dependencyIndex]?.id;
        if (parentTaskId && !allParentTasksRef.current.includes(parentTaskId)) {
          allParentTasksRef.current.push(parentTaskId);
        }
        if (task.usesAppIds) {
          task.deviceAppIds = [...deviceAppIds];
          const {fileAppIds} = task;
          if (fileAppIds?.length) {
            const appIds = fileAppIds.filter(fileAppId => deviceAppIds?.includes(fileAppId));
            if (!appIds.length) {
              incompatibleFiles.push(task.fileNameUI || task.fileLabel);
              task.fileName   = '';
              task.fileNameUI = '';
              task.fileLabel  = '';
              tasksUpdated = true;
              setJobNameUpdateEnabled?.(true);
            }
          }
          if ((task.appId !== '') && !deviceAppIds?.includes(task.appId)) {
            task.appId = '';
            tasksUpdated = true;
          }
          if ((task.appId === '') && deviceAppIds?.length === 1) {
            task.appId = deviceAppIds[0];
            tasksUpdated = true;
          }
        }
      });
      if (incompatibleFiles.length > 0) {
        let message = 'jobcreate:errors.taskFileNotCompatible';
        let files   = incompatibleFiles[0];
        if (incompatibleFiles.length > 1) {
          message = 'jobcreate:errors.taskFilesNotCompatible';
          files   = incompatibleFiles.join('\', \'');
        }
        dispatch(toggleUserPrompt({
          ...errorOptions,
          actionLabel : translate('common:ok'),
          message     : translate(message, {files})
        }));
      }
      if (tasksUpdated) {
        setTasks({tasks: jobTasks});
        onUpdateTasks({tasks: jobTasks});
      }
    }
  }, [deviceAppIds, JSON.stringify(taskValue)]);
  
  /**
   * invoked when a user wants to delete a task.
   * It takes in an index and the tasks array, remove the task at the given index,
   * updates the remaining tasks, and also updates dependencies accordingly.
   */
  const removeTask = useCallback(function(index, tasks) {
    const cloneTasks          = cloneDeep(tasks);
    const deletedTask         = tasks[index];
    const deletedTaskId       = deletedTask.id;
    const deletedTaskChildren = deletedTask.task.children;
    const parentTaskIndex     = deletedTask?.task?.metaData?.dependency?.index;
    if (!isUndefined(parentTaskIndex)) {
      const parentTaskChildren = tasks[parentTaskIndex]?.task?.children;
      const parentTaskId       = tasks[parentTaskIndex]?.id;
      removeItemFromArray(parentTaskChildren, deletedTaskId);
      if (!parentTaskChildren?.length) {
        removeItemFromArray(allParentTasksRef.current, parentTaskId);
        delete tasks[parentTaskIndex].trackLine;
      }
    }
    if (deletedTaskChildren?.length) {
      deletedTaskChildren?.forEach(child => {
        const task = tasks?.find(item => item.id === child);
        if (task) {
          task.ancestorIds.length = 0;
        }
      });
      removeItemFromArray(allParentTasksRef.current, deletedTaskId);
    }
    tasks.splice(index, 1);
    tasks?.forEach((item, i) => {
      item.task.index = i;
      item.task.name  = `task_${i}`;
    });
    const reorderedTasks = reorderTaskDependencies(cloneTasks, tasks);
    setTasks({tasks: reorderedTasks});
    onUpdateTasks({tasks: reorderedTasks});
    setJobNameUpdateEnabled?.(true);
  }, []);

  const handleDeviceFileType = useCallback(function({
    deviceTypeOption,
    fileTypeOption,
    taskIndex,
    tasks
  }) {
    const deviceType = {name: TaskField.DEVICE_TYPE, taskIndex};
    const fileType   = {name: TaskField.FILE_TYPE, taskIndex};
    const platformId = {name: TaskField.PLATFORM_ID, taskIndex};
    const usesAppIds = {name: TaskField.USES_APP_IDS, taskIndex};
    const hasVersion = isRequiredField(fileTypeOption, TaskField.VERSION);
    setSuppressNullVersions(hasVersion);
    updateTasks(deviceType, deviceTypeOption.name, tasks);
    updateTasks(fileType, fileTypeOption.name, tasks);
    updateTasks(platformId, fileTypeOption.platformId, tasks);
    updateTasks(usesAppIds, fileTypeUsesAppIds(fileTypeOption), tasks);
  }, []);

  const handleAppId = useCallback(function(index, tasks) {
    return function(e, value: string) {
      const appId = {name: TaskField.APP_ID, taskIndex: index};
      updateTasks(appId, value, tasks);
    };
  }, []);

  const handleFileName = useCallback(function(index, tasks) {
    return function(e, value: DeviceFile) {
      const fileSelf = value._self;
      const appIds = getFileAppIds(value);
      const {
        fileNameUI,
        fileLabel
      } = getFileNameDisplay(value);
      const currentTask  = tasks?.tasks[index] || {};
      const metaData     = {...currentTask?.task?.metaData, file: fileSelf.href, title: value.name, fileAction: 'Overwrite'};
      const fileAppIds   = {name: TaskField.FILE_APP_IDS, taskIndex: index};
      const fileNameType = {name: TaskField.FILE_NAME, fileNameUI, taskIndex: index};
      const fileMetaData = {name: TaskField.META_DATA, taskIndex: index};
      updateTasks(fileAppIds, appIds, tasks);
      updateTasks(fileNameType, fileLabel, tasks);
      updateTasks(fileMetaData, metaData, tasks);
    };
  }, []);

  const handleDependentTask = useCallback(function({
    taskIndex,
    selectedTask,
    tasks
  }) {
    const allTasks              = tasks?.tasks || [];
    const parentTaskIndex       = selectedTask-1;
    const parentTaskId          = allTasks[parentTaskIndex]?.id;
    const currentTaskId         = allTasks[taskIndex]?.id;
    const parentTaskChildren    = allTasks[parentTaskIndex]?.task?.children || [];
    const {dependency, ...rest} = allTasks[taskIndex]?.task?.metaData || {};
    const metaData              = {...rest};

    if (selectedTask) {
      if (!parentTaskChildren.includes(currentTaskId)) {
        if (dependency && (dependency?.index !== parentTaskIndex)) {
          const prevParent         = allTasks[dependency?.index];
          const prevParentChildren = prevParent?.task?.children || [];
          removeItemFromArray(prevParentChildren, currentTaskId);
          if (!prevParentChildren?.length && prevParent) {
            delete prevParent?.trackLine;
          }
        }
        const ancestorIds               = getAncestorIds(allTasks, parentTaskIndex);
        allTasks[taskIndex].ancestorIds = [...ancestorIds];
        parentTaskChildren.push(currentTaskId);
      }
      if (parentTaskId && !allParentTasksRef.current.includes(parentTaskId)) {
        allParentTasksRef.current.push(parentTaskId);
      }
      metaData.dependency = {
        index : parentTaskIndex
      };
    }
    const fileMetaData = {name: TaskField.META_DATA, taskIndex};
    updateTasks(fileMetaData, metaData, tasks);
  }, []);

  const removeDependentTask = useCallback(function({
    taskIndex,
    tasks
  }) {
    const allTasks           = tasks?.tasks || [];
    const currentTaskId      = allTasks[taskIndex]?.id;
    const parentTaskIndex    = allTasks[taskIndex]?.task?.metaData?.dependency?.index;
    const parentTaskId       = allTasks[parentTaskIndex].id;
    const parentTaskChildren = allTasks[parentTaskIndex]?.task?.children || [];
    removeItemFromArray(parentTaskChildren, currentTaskId);
    allTasks[taskIndex].ancestorIds.length = 0;
    if (!parentTaskChildren?.length) {
      removeItemFromArray(allParentTasksRef.current, parentTaskId);
      delete allTasks[parentTaskIndex].trackLine;
    }
    handleDependentTask({
      selectedTask : '',
      taskIndex,
      tasks
    });
  }, []);

  const updateTasks = useCallback(function(field, value, tasks) {
    const task       = tasks?.tasks[field.taskIndex].task;
    task[field.name] = value;
    if (task?.[TaskField.DEVICE_TYPE] && task?.[TaskField.FILE_TYPE] && task?.[TaskField.FILE_NAME]) {
      task.identifier = `${task?.[TaskField.DEVICE_TYPE]} ${task?.[TaskField.FILE_TYPE]} ${task?.[TaskField.FILE_NAME]}`;
    }
    if (field.name === TaskField.DEVICE_TYPE) {
      task.fileType   = '';
      task.appId      = '';
      task.fileAppIds = [];
      task.fileName   = '';
      task.fileNameUI = '';
      task.fileLabel  = '';
    } else if (field.name === TaskField.APP_ID) {
      if (!task.fileAppIds?.includes(value)) {
        task.fileAppIds = [];
        task.fileName   = '';
        task.fileNameUI = '';
        task.fileLabel  = '';
      }
    } else if (field.name === TaskField.FILE_APP_IDS) {
      if (task.usesAppIds) {
        if (!value?.includes(task.appId)) {
          let appId;
          value.find(fileAppId => {
            appId = task.deviceAppIds.find(deviceAppId => deviceAppId === fileAppId);
            return !!appId;
          });
          task.appId = appId || value?.[0] || '';
        }
      }
    } else if (field.name === TaskField.FILE_NAME) {
      task.fileNameUI = field.fileNameUI;
      task.fileLabel  = value;
      const {deviceType, fileType} = task;
      updateTaskValue({
        ...taskValue,
        [field.taskIndex] : (deviceType && fileType) ? `${deviceType} ${fileType}` : ''
      });
    }
    setTasks({tasks: tasks?.tasks});
    onUpdateTasks({tasks: tasks?.tasks});
    setJobNameUpdateEnabled?.(true);
  }, [taskValue]);

  const navigateRows = useCallback(function(index, navigate, tasks) {
    const task        = tasks?.tasks;
    const clonedTasks = cloneDeep(tasks?.tasks);
    const prevIndex   = index - 1;
    const nextIndex   = index + 1;
    if (navigate === 'up') {
      task[index]     = clonedTasks[prevIndex];
      task[prevIndex] = clonedTasks[index];
    }
    if (navigate === 'down') {
      task[index]     = clonedTasks[nextIndex];
      task[nextIndex] = clonedTasks[index];
    }
    const reorderedTasks = reorderTaskDependencies(clonedTasks, task);
    setTasks({tasks: reorderedTasks});
    onUpdateTasks({tasks: reorderedTasks});
    setJobNameUpdateEnabled?.(true);
  }, []);

  const jobTasks             = tasks?.tasks;
  const dependentTaskOptions = getDependentTaskOptions(jobTasks);

  const renderTaskContent = function(row: DownloadJobTasksElem, index) {
    return (
      <>
        <TableCell className={classes.taskOrder}>
          <JobTaskOrder row={row}
            taskOrder={index + 1}
            taskEndOrder={jobTasks?.length} />
        </TableCell>
        <TableCell>
          <JobTaskDeviceFileTypeChooser index={index}
            isFormattedText
            isFetching={isFetching}
            deviceTypes={deviceTypes}
            tasks={jobTasks}
            taskValue={taskValue}
            isDevicesSelected={isDevicesSelected}
            updateTaskValue={updateTaskValue}
            handleDeviceFileType={handleDeviceFileType} />
        </TableCell>
        <TableCell>
          <JobTaskAppIdChooser row={row}
            onChange={handleAppId(index, tasks)} />
        </TableCell>
        <TableCell>
          <JobTaskFileChooser row={row}
            accountId={accountId}
            suppressNullVersions={suppressNullVersions}
            includeSubAccounts={includeSubAccounts}
            isDevicesSelected={isDevicesSelected}
            devicePlatformIds={devicePlatformIds}
            onChange={handleFileName(index, tasks)} />
        </TableCell>
        <TableCell>
          <DependentTaskChooser taskIndex={index}
            tasks={jobTasks}
            isDevicesSelected={isDevicesSelected}
            dependentTaskOptions={dependentTaskOptions}
            handleDependentTask={handleDependentTask} />
        </TableCell>
        <TableCell className={classes.actionRowCell}>
          {isDevicesSelected &&
          <JobTaskRowEditor index={index}
            tasks={tasks}
            removeDependency={removeDependentTask}
            removeTask={removeTask}
            navigateRows={navigateRows} />
          }
        </TableCell>
      </>
    );
  };

  return (
    <div className={classes.actionsTile}>
      <TableContainer className={classes.tableContainer}>
        <Table>
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <span/>
              <TableCell className={classnames(classes.tableCell, classes.dragIconCell)} />
              <TableCell className={classnames(classes.tableCell, classes.taskOrder)}>
                {`${fieldTranslations?.taskOrder?.label}`}
              </TableCell>
              <TableCell className={classnames(classes.tableCell, classes.deviceTypeHeaderCell)}>
                {`${fieldTranslations?.deviceFileType?.label}`}
              </TableCell>
              <TableCell className={classnames(classes.tableCell, classes.appIdHeaderCell)}>
                {`${fieldTranslations?.appId?.label}`}
              </TableCell>
              <TableCell className={classes.tableCell}>
                {`${fieldTranslations?.fileName?.label}`}
              </TableCell>
              <TableCell className={classnames(classes.tableCell, classes.dependentTaskHeaderCell)}>
                {`${fieldTranslations?.dependentTask?.label}`}
              </TableCell>
              <TableCell className={classnames(classes.tableCell, classes.actionHeaderCell)} />
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            <Render.If condition={jobTasks?.[0]?.id}>
              <TasksDraggableList jobTasks={jobTasks}
                setTasks={setTasks}
                handleDependentTask={handleDependentTask}
                allParentTaskIds={allParentTasksRef.current}
                renderContent={renderTaskContent} />
            </Render.If>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default JobTasksGrid;
